/**
 * "About how long will my balance last?" for the balance panel.
 *
 * AT THE VISITOR'S OWN AVERAGE whenever there is one: 30-day spend over 30-day
 * session time, from the Usage page's summary. That figure already reflects
 * whichever engine and setting they actually use, so it beats any default.
 *
 * With no sessions at all, priced exactly as the cost calculator prices the
 * preset it opens on, on the engine (and Qwen region) it opens on, from the
 * same published rates and the same functions, so the two never show different
 * numbers for the same case. Deliberately NOT the wallet status's `rates`: those are the
 * conservative per-hour rates the server reserves a session's budget at, and
 * they would understate the hours several times over.
 */
import { costParts, engineRates, partsTotal, type Engine, type QwenRegion } from './cost-model';
import { DEFAULT_SCENARIO, scenarioById } from './calculator-scenarios';
import type { PricingResponse } from './pricing-client';

/**
 * USD per hour of the calculator's opening preset on `engine` (at `region`, for Qwen), or null
 * without rates, which includes Beijing while the payload lists no Beijing entry.
 */
export function defaultScenarioUsdPerHour(
  pricing: PricingResponse | null,
  engine: Engine,
  region: QwenRegion,
): number | null {
  const rates = engineRates(pricing, engine, region);
  if (!rates) return null;
  const preset = scenarioById(DEFAULT_SCENARIO);
  return partsTotal(costParts(rates, preset.settings, preset.sharedStream));
}

/** 30-day spend per session hour, or null with no session time or nothing spent. */
export function personalUsdPerHour(
  summary: { spendMicroUsd: number; sessionSeconds: number } | null
): number | null {
  if (!summary || summary.sessionSeconds <= 0 || summary.spendMicroUsd <= 0) return null;
  return summary.spendMicroUsd / 1_000_000 / (summary.sessionSeconds / 3600);
}

/** Hours the balance covers at `usdPerHour`; null when there is no rate. */
export function hoursFromBalance(balanceMicroUsd: number, usdPerHour: number | null): number | null {
  if (usdPerHour === null || usdPerHour <= 0) return null;
  return Math.max(0, balanceMicroUsd) / 1_000_000 / usdPerHour;
}

/**
 * Hours for display, always rounded DOWN: an estimate of how long money lasts
 * must not promise the last partial hour. Whole hours from ten up, one decimal
 * below, and "< 1" rather than a fraction of an hour.
 */
export function formatHours(hours: number, locale: string): string {
  if (hours <= 0) return '0';
  if (hours < 1) return '< 1';
  const value = hours < 10 ? Math.floor(hours * 10) / 10 : Math.floor(hours);
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value);
}
